import { PiBracketsCurly, TbCircles } from '@/components/react-icons/main';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import IconV2 from '@/components/v2/IconV2';
import doDebounce from '@/lib/debounce';
import { safeParseJsonValue } from '@/lib/safeParseJson';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import argumentDataSources from '@/schema/argumentDataSources';
import attributeDataTypeOptions from '@/screens/apps/schema/attributeDataTypeOptions';
import setWorkflowActiveInputState from '@/screens/workflows/redux/actions/setWorkflowActiveInputState';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import dataTypeIcons from '@/screens/workflows/schema/dataTypeIcons';
import { buildCssClassName } from '@shared/utils/buildCssClassName.js';
import { randomId } from '@shared/utils/randomId.js';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BlockNode } from './BlockNode';
import formatLexicalInitialValue from './formatLexicalInitialValue';
import LexicalComposer from './LexicalComposer.jsx';
import SuggestionsFloatingPane from '../v2/SuggestionsFloatingPane';
import { action } from '@/lib/api.js';

const primitiveOptions = attributeDataTypeOptions?.filter((n) => n.value !== 'array' && n.value !== 'object')?.map((n) => ({ ...n, dataType: 'array' }));

const dataTypeOptions = attributeDataTypeOptions?.map((n) => {
  if (n.value !== 'array') {
    return n;
  }

  return { ...n, children: [{ label: 'Any', value: 'array' }, ...primitiveOptions] };
});

const labelTitleMap = {
  organization: `your Organization's timezone settings`,
  credential: `your Connection's timezone settings`
};

const isObject = (obj) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null;

const LexicalInput = memo(({ trigger = 1, id, argKey, variation = 'default', value, inputStyle, onChange, debounce = 0, onDebounce, dataType, onFocus, onKeyPress, onKeyDown, onUseOptions, onUseOptionsTitle = 'Use Options', onTimezoneChange, placeholder, autoFocus, actions = [], showActions, useDataTypes = false, dataSource, argTimezone, timezone, suggestions, disabled = false, allowSelfOutput = false, multipleValuesIndex }) => {
  const [initialConfig, setInitialConfig] = useState({});
  const [editorState, setEditorState] = useState({});
  const [hasInit, setHasInit] = useState(false);
  const idRef = useRef(buildCssClassName(id || randomId()));
  const dataTypesRef = useRef(null);
  const timezoneRef = useRef(null);

  const stepDataSources = [argumentDataSources.workflowStep, argumentDataSources.workflowVariables, `${argumentDataSources.workflowStep}-customArguments`];
  const isAppActionInstance = dataSource === argumentDataSources.appActionInstance;
  const isWorkflowOrNoDataSource = stepDataSources.includes(dataSource) || !dataSource;
  const timezoneOptions = useSelector((state) => state.ui.timezones);
  const selectedStepId = useArgSelector(workflowScreenSelectors.activeInputStateByIdStepId);
  const selectedIndex = useArgSelector(workflowScreenSelectors.activeInputStateByIdIndex);
  const inputId = useArgSelector(workflowScreenSelectors.activeInputStateByIdInputId);

  const timezoneIsInherited = !!timezone;

  const initialType = (value?.type || 'lexical').replace('draft', 'lexical');

  const timezoneIcon = argTimezone ?? timezone?.value ?? 'EDT';

  const triggerDebounce = useCallback(doDebounce(onDebounce, debounce), [debounce]);

  const timezoneTitle = useMemo(() => {
    const title = 'Time Zone is ';

    const argTimezoneLabel = timezoneOptions?.find((n) => n.value === argTimezone)?.label;
    const timezoneLabel = timezoneOptions?.find((n) => n.value === timezone?.value)?.label;

    if (argTimezoneLabel) {
      return `${title}${argTimezoneLabel}`;
    }

    if (timezoneLabel) {
      return `${title}${timezoneLabel}, due to ${labelTitleMap[timezone?.label] || timezone?.label}`;
    }

    return `${title} Eastern Daylight Time`;
  }, [argTimezone, timezone]);

  useEffect(() => {
    if (typeof value === 'undefined' || value === null || !hasInit) {
      const deepValue = !isObject(value) ? value : 'value' in value && !('type' in value) ? value.value : value;
      const initialValue = formatLexicalInitialValue(deepValue);

      setInitialConfig({
        namespace: 'MyEditor',
        theme: {},
        onError: (error) => console.log(error),
        editorState: initialValue,
        nodes: [BlockNode],
        editable: !disabled
      });

      setEditorState(initialValue);

      if (!hasInit) {
        setHasInit(true);
      }
    }
  }, [value, disabled]);

  const handleChange = useCallback(
    (val) => {
      const valueJson = val.toJSON();
      const oldEditorState = editorState;
      const newEditorState = JSON.stringify(valueJson);
      setEditorState(newEditorState);
      if (oldEditorState === newEditorState) {
        return;
      }
      const finalState = { ...value, type: isAppActionInstance ? 'lexical' : initialType, value: valueJson };
      onChange?.(finalState);
      if (onDebounce) {
        triggerDebounce(finalState);
      }
    },
    [onChange, onDebounce, editorState, initialType, value]
  );

  const handleSuggestionChange = useCallback(
    (suggestionValue) => {
      const newEditorState = formatLexicalInitialValue(suggestionValue);

      setEditorState(newEditorState);

      const parsedValue = safeParseJsonValue(newEditorState, newEditorState);
      const finalState = {
        ...value,
        type: initialType,
        value: parsedValue
      };

      if (onChange) {
        onChange?.(finalState);
        setTimeout(() => {
          action('SET_EXTERNAL_UPDATES', [{ key: `lexicalInput-${argKey}`, timestamp: new Date() }]);
        }, 0);
      }

      if (onDebounce) {
        triggerDebounce(finalState);
      }
    },
    [onChange, onDebounce, editorState, initialType, value]
  );

  // Mark this input as the last active input.
  const handleFocusOrKeypress = useCallback(() => {
    if (inputId !== idRef.current && isWorkflowOrNoDataSource) {
      setWorkflowActiveInputState?.({ inputId: idRef.current, inputArgKey: argKey, allowSelfOutput, multipleValuesIndex });
    }
  }, [idRef.current, inputId, allowSelfOutput, multipleValuesIndex]);

  const handleFocus = useCallback(
    (e) => {
      handleFocusOrKeypress(e);
      onFocus?.(e);
    },
    [handleFocusOrKeypress, onFocus]
  );

  const handleKeyPress = useCallback(
    (e) => {
      handleFocusOrKeypress(e);
      onKeyPress?.(e);
    },
    [handleFocusOrKeypress, onKeyPress]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        if (isWorkflowOrNoDataSource) {
          if (selectedIndex !== null) {
            setWorkflowActiveInputState?.({ index: null });
          } else if (selectedStepId !== null) {
            setWorkflowActiveInputState?.({ stepId: null });
          }
        }

        document.querySelector(inputId ? `.input-element.li-${inputId}` : '.input-element')?.focus();
        return;
      }

      onKeyDown?.(e);
    },
    [setWorkflowActiveInputState, onKeyDown, inputId, selectedIndex, selectedStepId, isWorkflowOrNoDataSource]
  );

  const handleDataType = (e) => {
    const result = {};

    if (e?.dataType === 'array') {
      result.dataType = 'array';
      result.itemDataType = e?.value;
    } else {
      result.dataType = e?.value;
    }

    const finalState = {
      ...value,
      type: initialType,
      value: safeParseJsonValue(editorState, editorState),
      ...result
    };

    onChange?.(finalState);

    if (onDebounce) {
      triggerDebounce(finalState);
    }
  };

  const handleTimezone = (e) => {
    onTimezoneChange?.(e.value);
  };

  return (
    <>
      <div className={`lexical-input-wrapper ${idRef.current} ${variation} ${dataType ? 'has-datatype' : ''} ${disabled ? 'disabled' : ''}`} key={trigger}>
        {dataType && (
          <div className={`lexical-input-data-type ${dataType?.toLowerCase()}`}>
            <IconV2 icon={dataTypeIcons[dataType]} title={dataType} />
          </div>
        )}
        {hasInit && Boolean(trigger) && (
          <LexicalComposer //
            initialConfig={initialConfig}
            id={idRef.current}
            className={idRef.current}
            placeholder={placeholder}
            inputStyle={inputStyle}
            autoFocus={autoFocus}
            handleChange={handleChange}
            handleFocus={handleFocus}
            handleKeyDown={handleKeyDown}
            handleKeyPress={handleKeyPress}
          />
        )}
        <div className={`lexical-input-actions ${showActions ? 'always-show' : ''}`}>
          {useDataTypes && (
            <div ref={dataTypesRef}>
              <IconV2 icon={TbCircles} variant={value?.dataType || dataType ? 'light-medium' : 'inactive'} title={'Change Argument Data Type'} />
              {!disabled && <FloatingSelectV2 triggers={[dataTypesRef]} nullable={true} value={value?.dataType || dataType} options={dataTypeOptions} onChange={handleDataType} useSearch={false} />}
            </div>
          )}
          {timezone && (
            <div ref={timezoneRef} style={{ marginTop: '-1px' }}>
              <span className={`timezone-icon${argTimezone ? ' active' : ''} ${timezoneIsInherited ? 'inherited' : ''}`} data-title={timezoneTitle}>
                {timezoneIcon}
              </span>
              <FloatingSelectV2 triggers={[timezoneRef]} nullable={true} value={argTimezone?.value} options={timezoneOptions} onChange={handleTimezone} useSearch={true} />
            </div>
          )}
          {onUseOptions && <IconV2 icon={PiBracketsCurly} title={onUseOptionsTitle} onClick={disabled ? null : onUseOptions} className={'inactive'} />}
          {suggestions && <SuggestionsFloatingPane id={idRef.current} alignWith={`.${idRef.current}`} suggestions={suggestions} onSuggestionChange={(e) => handleSuggestionChange(e.target.value)} />}
          {actions?.map((n, i) => {
            if (n?.popover) {
              return (
                <n.popover key={i} align={'end'} value={n?.value} onChange={n?.onChange}>
                  <div>
                    <IconV2 key={i} icon={n.icon} variant={`inactive ${n.className}`} onClick={disabled ? null : n.onClick} title={n?.title || null} />
                  </div>
                </n.popover>
              );
            }

            return <IconV2 key={i} icon={n.icon} variant={`inactive ${n.className}`} onClick={disabled ? null : n.onClick} title={n?.title || null} />;
          })}
        </div>
      </div>
    </>
  );
});

LexicalInput.displayName = 'LexicalInput';

export default LexicalInput;
